import ExcelJS from "exceljs";
import moment from "moment";

const purchasedProductOf = (row) => {
  const raw = row?.purchasedProduct;
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) || {};
    } catch (e) {
      return {};
    }
  }
  return typeof raw === "object" ? raw : {};
};

/** Purchased article as "<id> – <description>", matching the drawer. */
const articleIdDesc = (row) => {
  const purchased = purchasedProductOf(row);
  const id =
    purchased.articleId || pick(row, ["articleId", "conversionArticleId"]);
  const description =
    purchased.articleDescription ||
    pick(row, [
      "articleDescription",
      "articleDesc",
      "conversionArticleDesc",
      "conversionArticleDescription",
    ]);
  return [id, description].filter(Boolean).join(" - ").trim();
};


const pick = (row, paths) => {
  for (const path of paths) {
    const value = path
      .split(".")
      .reduce((acc, key) => (acc == null ? acc : acc[key]), row);
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return "";
};

const formatDate = (value) => {
  if (!value) return "";
  const m = moment(
    value,
    [
      // convertedAt arrives as "DD-MM-YYYY HH:mm:ss" — keep the datetime forms first.
      "DD-MM-YYYY HH:mm:ss",
      "DD-MM-YYYY HH:mm",
      "DD/MM/YYYY HH:mm:ss",
      "DD/MM/YYYY HH:mm",
      "YYYY-MM-DD HH:mm:ss",
      "DD-MM-YYYY",
      "DD/MM/YYYY",
      "YYYY-MM-DD",
      moment.ISO_8601,
    ],
    true
  );
  if (m.isValid()) return m.format("DD-MM-YYYY");
  const loose = moment(value);
  return loose.isValid() ? loose.format("DD-MM-YYYY") : "";
};

const fullName = (row) => {
  const combined = pick(row, [
    "customerName",
    "customer.name",
    "custName",
    "name",
  ]);
  if (combined) return combined;
  const first = pick(row, [
    "customer.firstName",
    "firstName",
    "customerFirstName",
    "custFirstName",
  ]);
  const last = pick(row, [
    "customer.lastName",
    "lastName",
    "customerLastName",
    "custLastName",
  ]);
  return [first, last].filter(Boolean).join(" ").trim();
};

const storeText = (row, storePaths, codePaths) => {
  const store = pick(row, storePaths);
  const isObject = store && typeof store === "object";
  const name = isObject ? store.name : store;
  const code = (isObject && store.code) || pick(row, codePaths);
  const parts = [name, code]
    .filter((part) => part !== undefined && part !== null && part !== "")
    .map(String);
  // Guard against the name already carrying the code (" ... · 8971").
  if (parts.length === 2 && parts[0].includes(parts[1])) return parts[0];
  return parts.join(" · ").trim();
};

const amountValue = (row) => {
  const purchased = purchasedProductOf(row);
  const raw =
    purchased.amount !== undefined &&
    purchased.amount !== null &&
    purchased.amount !== ""
      ? purchased.amount
      : pick(row, [
          "amount",
          "purchaseAmount",
          "purchasedProductAmount",
          "conversionAmount",
        ]);
  if (raw === "") return "";
  const num = Number(String(raw).replace(/[^0-9.-]/g, ""));
  return Number.isNaN(num) ? String(raw) : num;
};

const displayLength = (value, type) => {
  if (type === "number" && typeof value === "number") {
    return `₹${value.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`.length;
  }
  return String(value ?? "").length;
};


export const LEAD_DOWNLOAD_COLUMNS = [
  { header: "Region", get: (r) => pick(r, ["region", "store.region", "siteRegion", "regionName"]) },
  { header: "Cluster", get: (r) => pick(r, ["cluster", "store.cluster", "clusterName", "clusterCode", "storeCluster", "cluster_name", "customer.cluster", "site.cluster", "siteCluster"]) },
  { header: "Store Code", get: (r) => pick(r, ["storeCode", "store", "siteCode", "store.code", "storeId"]) },
  { header: "Created Name", get: (r) => pick(r, ["createdName", "createdByName", "creatorName", "createdUserName", "createdEmployeeName", "createdByEmployeeName", "employeeName", "createdNameOfEmployee", "createdByUserName", "createdUser", "createdByUser", "creator", "createdEmployee"]) },
  { header: "Created by", get: (r) => pick(r, ["createdBy", "customer.createdBy"]) },
  { header: "Last Updated by", get: (r) => pick(r, ["updatedBy", "lastUpdatedBy", "lastModifiedBy", "modifiedBy", "customer.updatedBy"]) },
  { header: "Enquiry Date", type: "date", get: (r) => formatDate(pick(r, ["enquiryDate", "dateOfEnquiry", "enquiry_date", "leadCreatedDate", "leadDate", "enquiryDateTime", "createdAt", "createdDate"])) },
  { header: "Expected Date", type: "date", get: (r) => formatDate(pick(r, ["expectedPurchaseDate", "expectedDateOfPurchase", "expectedDate", "expected_purchase_date", "expectedPurchase", "purchaseDate"])) },
  { header: "Customer Name", get: (r) => fullName(r) },
  { header: "Customer Mobile No", type: "text", get: (r) => String(pick(r, ["customerContactNo",]) || "") },
  { header: "Email ID", get: (r) => pick(r, ["customerEmail"]) },
  { header: "Family", get: (r) => pick(r, ["category"]) },
  { header: "Brand", get: (r) => pick(r, ["brand"]) },
  { header: "Status", get: (r) => pick(r, ["status"]) },
  { header: "Reason", get: (r) => pick(r, ["reason"]) },
  { header: "Others", get: (r) => pick(r, ["others"]) },
  { header: "Store Remarks", get: (r) => pick(r, ["remarksStore"]) },
  { header: "Created store", get: (r) => storeText(r, [ "createdStore", "createdStoreName"], ["storeCode", "createdStoreCode"]) },
  { header: "Converted store", get: (r) => storeText(r, [ "convertedStore", "convertedStoreName"], ["convertedStoreCode"]) },
  { header: "Converted Purchase Date", type: "date", get: (r) => formatDate(pick(r, ["convertedAt"])) },
  { header: "Invoice number", type: "text", get: (r) => String(pick(r, ["invoiceNumber"]) || "") },
  { header: "Article ID & Description", type: "text", get: (r) => articleIdDesc(r) },
  { header: "Amount", type: "number", get: (r) => amountValue(r) },
];
// Missing values must be null, not "": ExcelJS writes "" as a real (empty) string
// cell, which Excel treats as populated — Ctrl+Shift+↓ then runs past the data and
// pivot "Count of" fields count every row instead of only the filled ones.
const toCellValue = (value) => {
  if (value === undefined || value === null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  return value;
};
const buildRows = (content) =>
  (content || []).map((row) =>
    LEAD_DOWNLOAD_COLUMNS.map((col) => toCellValue(col.get(row)))
  );

const triggerDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const HEADER_FILL = "FF1F3A8A"; // template header blue
const BORDER_COLOR = "FFD9D9D9"; // light gray data borders
const MIN_COL_WIDTH = 12;
const MAX_COL_WIDTH = 50;
const DATA_ROW_HEIGHT = 18; // fixed minimum height, no collapse

export const exportLeadsToExcel = async (content, filename) => {
  const rows = buildRows(content);

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Leads", {
    views: [{ state: "frozen", ySplit: 1 }], // freeze header on scroll
  });

  const headers = LEAD_DOWNLOAD_COLUMNS.map((c) => c.header);
  sheet.addRow(headers);
  rows.forEach((r) => sheet.addRow(r));

  // Auto-fit: width follows the longest of header / cell content per column.

LEAD_DOWNLOAD_COLUMNS.forEach((col, index) => {
  const column = sheet.getColumn(index + 1);

  let maxLen = col.header.length;

  rows.forEach((r) => {
    const len = displayLength(r[index], col.type);
    if (len > maxLen) {
      maxLen = len;
    }
  });

  // Auto width with extra padding
  let width = Math.max(maxLen + 5, MIN_COL_WIDTH);

  // Custom widths for long text columns
  if (col.header === "Reason") {
    width = Math.max(width, 30);
  }

  if (col.header === "Store Remarks") {
    width = Math.max(width, 40);
  }

  if (col.header === "Others") {
    width = Math.max(width, 25);
  }

  if (col.header === "Article ID & Description") {
    width = Math.max(width, 35);
  }

  if (col.header === "Created store" || col.header === "Converted store") {
    width = Math.max(width, 22);
  }

  // Limit maximum width
  column.width = Math.min(width, 60);

  if (col.type === "text") {
    column.numFmt = "@";
  }

  if (col.type === "number") {
    column.numFmt = '"₹"#,##,##0.00'; // en-IN grouping, same convention as the UI
  }
});

  const thinBorder = {
    top: { style: "thin", color: { argb: BORDER_COLOR } },
    left: { style: "thin", color: { argb: BORDER_COLOR } },
    bottom: { style: "thin", color: { argb: BORDER_COLOR } },
    right: { style: "thin", color: { argb: BORDER_COLOR } },
  };

  // Header styling
  const headerRow = sheet.getRow(1);
  headerRow.height = 22;
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: HEADER_FILL },
    };
    cell.alignment = { vertical: "middle", horizontal: "left", wrapText: false };
    cell.border = thinBorder;
  });
  
  // Auto-filter across the whole header range
  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: LEAD_DOWNLOAD_COLUMNS.length },
  };

  // Data rows: fixed height, single line, light borders
  for (let i = 0; i < rows.length; i++) {
    const excelRow = sheet.getRow(i + 2);
    excelRow.height = DATA_ROW_HEIGHT;
    excelRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.alignment = { vertical: "middle", horizontal: "left", wrapText: false };
      cell.border = thinBorder;
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  triggerDownload(blob, filename);
};

const escapeCsv = (value) => {
  const str = String(value ?? "");
  return `"${str.replace(/"/g, '""')}"`;
};

/** Plain CSV from the same column config — unaffected by Excel formatting. */
export const exportLeadsToCSV = (content, filename) => {
  const rows = buildRows(content);
  const headerLine = LEAD_DOWNLOAD_COLUMNS.map((c) => escapeCsv(c.header)).join(",");
  const dataLines = rows.map((r) => r.map(escapeCsv).join(","));
  const csvContent = [headerLine, ...dataLines].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  triggerDownload(blob, filename);
};